// AssignmentModal component - Three-step mission assignment workflow
// CHANGE: Added apikey header to Edge Function fetch call for gateway compatibility

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getPortalRoot } from '../../lib/portalUtils';
import { X, ChevronRight, Calendar as CalendarIcon, Users, CheckCircle, Repeat, AlertTriangle, Clock, MapPin } from 'lucide-react';
import type { TemplateWithItems } from '../../lib/templates';
import type { User, Site } from '../../lib/supabase';
import { assignTaskToUser, getAssignedTasks } from '../../lib/templates';
import { formatRecurrenceText } from '../../lib/recurrence';
import { formatDate, formatDuration } from '../../lib/dateUtils';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { Timeline } from './Timeline';
import { WizardProgressIndicator } from './WizardProgressIndicator';
import { supabase } from '../../lib/supabase';
import { envConfig } from '../../lib/envConfig';
import { DatePicker } from '../ui/DatePicker';
import { CustomSelect } from '../ui/CustomSelect';
import { TimePicker } from '../ui/TimePicker';
import { SearchableUserSelect } from '../ui/SearchableUserSelect';
import { useToast } from '../../hooks/useToast';

interface AssignmentModalProps {
  template: TemplateWithItems;
  users: User[];
  sites: Site[];
  onClose: () => void;
  onAssigned: () => void;
}

interface UserWithTasks extends User {
  tasks: {
    title: string;
    startTime: string;
    duration: number;
    status?: 'not_started' | 'completed' | 'overdue';
  }[];
}

type Step = 'site-user' | 'planning' | 'confirmation';

export function AssignmentModal({ template, users, sites, onClose, onAssigned }: AssignmentModalProps) {
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState<Step>('site-user');

  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  const [siteUsers, setSiteUsers] = useState<User[]>([]);
  const [userTasks, setUserTasks] = useState<UserWithTasks['tasks']>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);

  const [scheduledTime, setScheduledTime] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  useEffect(() => {
    const today = formatDate(new Date());
    setSelectedDate(today);
  }, []);

  useEffect(() => {
    if (selectedSiteId && currentStep === 'site-user') {
      loadSiteUsers();
    }
  }, [selectedSiteId]);

  useEffect(() => {
    if (selectedUserId && selectedDate && currentStep === 'planning') {
      loadUserTasks();
    }
  }, [selectedUserId, selectedDate, currentStep]);

  const loadSiteUsers = async () => {
    setLoadingUsers(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        setError('Session non valide');
        setLoadingUsers(false);
        return;
      }

      const userSitesResponse = await fetch(
        `${envConfig.supabaseUrl}/functions/v1/get-all-user-sites`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            'apikey': envConfig.supabaseAnonKey,
          },
        }
      );

      const userSitesResult = await userSitesResponse.json();
      const userSitesData = userSitesResult.success ? userSitesResult.data : [];

      const userIdsForSite = userSitesData
        .filter((us: any) => us.site_id === selectedSiteId)
        .map((us: any) => us.user_id);

      const filteredUsers = users.filter(u =>
        userIdsForSite.includes(u.id) && u.is_active
      );

      setSiteUsers(filteredUsers);
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadUserTasks = async () => {
    setLoadingTasks(true);
    setError('');
    try {
      const assignedTasks = await getAssignedTasks({
        siteId: selectedSiteId,
        dateFrom: selectedDate,
        dateTo: selectedDate
      });

      const tasks = assignedTasks
        .filter(task => task.assigned_to_user_id === selectedUserId)
        .map(task => ({
          title: task.template?.name || 'Tâche',
          startTime: task.scheduled_time || '09:00',
          duration: (task as any).duration_override || task.template?.estimated_duration_minutes || 30,
          status: task.status
        }));

      setUserTasks(tasks);
    } catch (err) {
      setError('Erreur lors du chargement des tâches');
    } finally {
      setLoadingTasks(false);
    }
  };

  const getRecurrenceDateWarning = (): string | null => {
    if (!template.recurrence || !selectedDate) return null;

    const selected = new Date(selectedDate);
    const dayOfWeek = selected.getDay() === 0 ? 7 : selected.getDay();

    if (template.recurrence.frequency === 'weekly' && template.recurrence.days_of_week && template.recurrence.days_of_week.length > 0) {
      if (!template.recurrence.days_of_week.includes(dayOfWeek)) {
        const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
        const selectedDayName = dayNames[dayOfWeek - 1];
        const expectedDays = template.recurrence.days_of_week.map(d => dayNames[d - 1]).join(', ');
        return `Attention : Vous avez sélectionné un ${selectedDayName}, mais cette mission est configurée pour se répéter les ${expectedDays}.`;
      }
    }

    if (template.recurrence.frequency === 'monthly' && template.recurrence.day_of_month) {
      const selectedDay = selected.getDate();
      if (selectedDay !== template.recurrence.day_of_month) {
        return `Attention : Vous avez sélectionné le ${selectedDay} du mois, mais cette mission est configurée pour se répéter le ${template.recurrence.day_of_month} de chaque mois.`;
      }
    }

    if (template.recurrence.frequency === 'yearly') {
      const selectedMonth = selected.getMonth() + 1;
      const selectedDay = selected.getDate();

      if (template.recurrence.month_of_year && selectedMonth !== template.recurrence.month_of_year) {
        const monthNames = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
        const selectedMonthName = monthNames[selectedMonth - 1];
        const expectedMonthName = monthNames[template.recurrence.month_of_year - 1];
        return `Attention : Vous avez sélectionné une date en ${selectedMonthName}, mais cette mission est configurée pour se répéter en ${expectedMonthName}.`;
      }

      if (template.recurrence.day_of_month && selectedDay !== template.recurrence.day_of_month) {
        return `Attention : Vous avez sélectionné le ${selectedDay}, mais cette mission est configurée pour se répéter le ${template.recurrence.day_of_month} du mois.`;
      }
    }

    return null;
  };

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    setError('');
    setCurrentStep('planning');
  };

  const handlePlanningNext = () => {
    if (!selectedDate) {
      setError('Veuillez sélectionner une date');
      return;
    }

    const selected = new Date(selectedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selected < today) {
      setError('La date ne peut pas être dans le passé');
      return;
    }

    if (!scheduledTime) {
      setError('Veuillez sélectionner une heure de début');
      return;
    }

    setError('');
    setCurrentStep('confirmation');
  };

  const handleAssign = async () => {
    setLoading(true);

    try {
      const hasRecurrence = !!template.recurrence;

      const assignmentData = {
        userId: selectedUserId,
        siteId: selectedSiteId,
        date: selectedDate,
        time: scheduledTime,
        // Recurrence fields - automatically activated if template has recurrence
        isRecurring: hasRecurrence,
        recurrenceRuleId: hasRecurrence ? template.recurrence!.id : undefined,
        recurrenceEndDate: undefined // No longer configurable
      };

      await assignTaskToUser(template.id, assignmentData);

      showToast(
        `Mission "${template.name}" attribuée à ${selectedUser?.first_name} ${selectedUser?.last_name}`,
        'success'
      );

      onAssigned();
      onClose();
    } catch (err: any) {
      // Provide specific error messages
      if (err?.message === 'Template has no items') {
        setError('Cette mission ne contient aucune tâche. Veuillez ajouter des tâches avant de l\'attribuer.');
      } else if (err?.message === 'Template not found') {
        setError('Mission introuvable.');
      } else if (err?.message === 'Scheduling conflict detected' && err?.conflictingTask) {
        const conflict = err.conflictingTask;
        setError(
          `Impossible d'attribuer la mission à cet horaire. La mission "${conflict.title}" est déjà programmée de ${conflict.startTime} à ${conflict.endTime}. Veuillez choisir un autre horaire.`
        );
      } else {
        setError('Une erreur est survenue lors de l\'attribution');
      }

      setLoading(false);
    }
  };

  const hasUnsavedChanges = (): boolean => {
    return selectedSiteId !== '' || selectedUserId !== '' || scheduledTime !== '';
  };

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesDialog(true);
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    setError('');
    if (currentStep === 'planning') {
      setCurrentStep('site-user');
      setSelectedUserId('');
      setScheduledTime('');
      setUserTasks([]);
    } else if (currentStep === 'confirmation') {
      setCurrentStep('planning');
    }
  };

  const handleStepClick = (step: number) => {
    if (step === 1 && currentStep !== 'site-user') {
      setCurrentStep('site-user');
      setSelectedUserId('');
      setScheduledTime('');
      setUserTasks([]);
      setError('');
    } else if (step === 2 && currentStep === 'confirmation' && selectedUserId) {
      setCurrentStep('planning');
      setError('');
    }
  };

  const selectedSite = sites.find(s => s.id === selectedSiteId);
  const selectedUser = siteUsers.find(u => u.id === selectedUserId) || users.find(u => u.id === selectedUserId);

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleCloseAttempt()} role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Attribuer une mission</h3>
            <p className="text-sm text-gray-500 mt-1">{template.name}</p>
          </div>
          <button
            onClick={handleCloseAttempt}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label="Fermer"
          >
            <X size={24} />
          </button>
        </div>

        <div className="px-6 pt-4">
          <WizardProgressIndicator
            currentStep={currentStep === 'site-user' ? 1 : currentStep === 'planning' ? 2 : 3}
            steps={[
              { number: 1, title: 'Site & Utilisateur', completed: currentStep !== 'site-user' },
              { number: 2, title: 'Planification', completed: currentStep === 'confirmation' },
              { number: 3, title: 'Confirmation', completed: false }
            ]}
            onStepClick={handleStepClick}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {currentStep === 'site-user' && (
            <div className="space-y-5">
              {template.recurrence && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Repeat className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">
                        Mission récurrente
                      </h4>
                      <p className="text-sm text-blue-700 font-medium">
                        {formatRecurrenceText(template.recurrence)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="site-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Site <span className="text-red-500">*</span>
                </label>
                <CustomSelect
                  value={selectedSiteId}
                  onChange={setSelectedSiteId}
                  options={sites.map(site => ({
                    value: site.id,
                    label: site.name
                  }))}
                  placeholder="Sélectionnez un site"
                />
              </div>

              {selectedSiteId && (
                <>
                  {loadingUsers ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-gray-500">Chargement des utilisateurs...</div>
                    </div>
                  ) : siteUsers.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
                      Aucun utilisateur actif trouvé pour ce site
                    </div>
                  ) : (
                    <SearchableUserSelect
                      users={siteUsers}
                      selectedUserId={selectedUserId}
                      onUserSelect={handleUserSelect}
                      label="Utilisateur"
                      placeholder="Sélectionner un utilisateur..."
                      required
                    />
                  )}
                </>
              )}
            </div>
          )}

          {currentStep === 'planning' && selectedUser && (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={14} className="text-blue-600 flex-shrink-0" />
                    <span className="text-gray-600">Site:</span>
                    <span className="text-gray-900 font-semibold">{selectedSite?.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={14} className="text-blue-600 flex-shrink-0" />
                    <span className="text-gray-600">Utilisateur:</span>
                    <span className="text-gray-900 font-semibold">{`${selectedUser.first_name} ${selectedUser.last_name}`}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <DatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  label="Date"
                  required
                />
                <TimePicker
                  value={scheduledTime}
                  onChange={setScheduledTime}
                  label="Heure de début"
                  required
                  step={15}
                />
              </div>

              {getRecurrenceDateWarning() && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle size={18} className="flex-shrink-0 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    {getRecurrenceDateWarning()}
                  </p>
                </div>
              )}

              {loadingTasks ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-gray-500">Chargement de la timeline...</div>
                </div>
              ) : (
                <div className="border-t border-gray-200 pt-4">
                  <div className="h-[300px]">
                    <Timeline
                      existingTasks={userTasks.map((task, idx) => ({
                        id: `existing-${idx}`,
                        title: task.title,
                        startTime: task.startTime,
                        duration: task.duration,
                        status: task.status
                      }))}
                      newTask={{
                        title: template.name,
                        duration: template.estimated_duration_minutes || 30
                      }}
                      newTaskTime={scheduledTime}
                      onNewTaskTimeChange={setScheduledTime}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {currentStep === 'confirmation' && selectedUser && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle size={16} className="text-blue-600" />
                  Récapitulatif de l'attribution
                </h4>
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">Site:</span>
                    <span className="text-gray-900 font-semibold flex-1">{selectedSite?.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={14} className="text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">Utilisateur:</span>
                    <span className="text-gray-900 font-semibold">{`${selectedUser.first_name} ${selectedUser.last_name}`}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarIcon size={14} className="text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">Date:</span>
                    <span className="text-gray-900 font-semibold flex-1">
                      {new Date(selectedDate).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">Heure de début:</span>
                    <span className="text-gray-900 font-semibold flex-1">{scheduledTime}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">Durée estimée:</span>
                    <span className="text-gray-900 font-semibold flex-1">{formatDuration(template.estimated_duration_minutes || 30)}</span>
                  </div>
                  {template.recurrence && (
                    <div className="flex items-start gap-2 mt-2 pt-2 border-t border-blue-200">
                      <Repeat size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <span className="text-gray-700 block mb-1">Récurrence:</span>
                        <span className="text-gray-900 font-semibold">{formatRecurrenceText(template.recurrence)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>

        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={currentStep === 'site-user' ? handleCloseAttempt : handleBack}
            disabled={loading}
            className="px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentStep === 'site-user' ? 'Annuler' : 'Retour'}
          </button>

          {currentStep === 'planning' && (
            <button
              type="button"
              onClick={handlePlanningNext}
              disabled={!scheduledTime || loading}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Suivant</span>
              <ChevronRight size={16} />
            </button>
          )}

          {currentStep === 'confirmation' && (
            <button
              type="button"
              onClick={handleAssign}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle size={16} />
              <span>{loading ? 'En cours...' : 'Attribuer la mission'}</span>
            </button>
          )}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={showUnsavedChangesDialog}
        title="Modifications non enregistrées"
        message="Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter sans enregistrer ?"
        confirmLabel="Quitter sans enregistrer"
        cancelLabel="Continuer à modifier"
        onConfirm={onClose}
        onCancel={() => setShowUnsavedChangesDialog(false)}
        variant="warning"
      />
    </div>,
    getPortalRoot()
  );
}
